import React, { memo, useRef, useState, useEffect } from 'react'
import { CircularProgress, Box, Typography, IconButton, Tooltip, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import TranslationProgress from './components/TranslationProgress'
import ViewHtml from '../html-viewer/ViewHtml'
import ViewPdf from '../pdf-viewer/ViewPdf'
import { isMobile } from '../../utils/utils'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { backendClient } from '../../api/backend'

// https://<account>.blob.core.windows.net/<container>/<blob>?<sas> → fresh SAS URL (or null)
const resignBlobUrl = async (url) => {
  try {
    const { hostname, pathname } = new URL(url)
    if (!hostname.endsWith('.blob.core.windows.net')) return null
    const [containerName, ...blobParts] = pathname.replace(/^\//, '').split('/')
    const blobName = decodeURIComponent(blobParts.join('/'))
    if (!containerName || !blobName) return null
    const data = await backendClient.getDocumentSas(containerName, blobName)
    return data?.blob_url_with_sas || null
  } catch (e) {
    return null
  }
}

const DocumentViewer = memo(
  ({ document, documentSourceUrl, isLoadingDocument, selectedCitation, setDocument, showTranslationMenu = true }) => {
    console.log('[DocumentViewer] render', {
      docId: document?.id,
      citationId: selectedCitation?.id,
      section_title: selectedCitation?.metadata?.section_title,
      isLoadingDocument,
      hasUrl: !!document?.url,
    })
    const theme = useTheme()
    const { t, i18n } = useTranslation()
    const currentLang = i18n.language
    const [isTitleExpanded, setIsTitleExpanded] = useState(false)

    const isTranslationInProgress = useSelector(
      state => state.document.isTranslationInProgress
    )
    const translationProgress = useSelector(
      state => state.document.translationProgress
    )

    const iframeRef = useRef(null)
    const [htmlContent, setHtmlContent] = useState('')

    // Reset title expansion when document changes
    useEffect(() => {
      setIsTitleExpanded(false)
    }, [document?.id])

    useEffect(() => {
      const fetchHtmlDocumentContent = async () => {
        let response = await fetch(document?.url)
        // SAS links are short-lived and the last opened document is restored from
        // persisted state: re-sign an expired link instead of rendering Azure's error XML.
        if (!response.ok) {
          const fresh = await resignBlobUrl(document.url)
          response = fresh ? await fetch(fresh) : response
        }
        setHtmlContent(response.ok ? await response.text() : '')
      }

      if (document?.url && isHtmlDocument(document)) {
        fetchHtmlDocumentContent()
      }
    }, [document?.url])

    // Helper to determine if document is PDF or HTML
    const isPdfDocument = (doc) => {
      console.log('[DEBUG] isPdfDocument - doc:', doc)
      if (!doc?.url && !doc?.metadata) return false
      const url = doc?.url || ''
      // Check URL for PDF extension (handle query parameters)
      const urlPath = url.split('?')[0] // Remove query parameters
      const isPdfUrl = urlPath.toLowerCase().endsWith('.pdf')

      // Check metadata doc_type
      const isPdfDocType = doc?.metadata?.doc_type === 'pdf'

      return isPdfUrl || isPdfDocType
    }

    const isHtmlDocument = (doc) => {
      if (!doc?.url && !doc?.metadata) return false
      const url = doc?.url || ''

      // Check URL for HTML extension (handle query parameters)
      const urlPath = url.split('?')[0] // Remove query parameters
      const isHtmlUrl = urlPath.toLowerCase().endsWith('.html')

      // Check metadata doc_type
      const isHtmlDocType = doc?.metadata?.doc_type === 'html'

      return isHtmlUrl || isHtmlDocType
    }

    const handleTitleToggle = () => {
      setIsTitleExpanded(!isTitleExpanded)
    }

    // Check if source_url contains fedlex and modify title accordingly
    const baseTitle = document ? (selectedCitation?.metadata?.title) : "-"
    const title = selectedCitation?.metadata?.source_url && selectedCitation?.metadata?.source_url.toLowerCase().includes('fedlex')
      ? `Fedlex - ${baseTitle}`
      : baseTitle
    const isMobileView = isMobile()
    const shouldShowExpandButton = isMobileView && title && title.length > 50 // Show expand button for long titles on mobile

    return (
      <Box
        dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        }}
      >
        {/* Document Header */}
        <Box sx={{
          p: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1
          }}>
            <Tooltip title={title || ''} arrow enterDelay={300} disableHoverListener={!title || title.length <= 80}>
              <Typography
                variant="h8"
                fontWeight={600}
                color={theme.palette.text.primary}
                sx={{
                  flex: 1,
                  cursor: shouldShowExpandButton ? 'pointer' : 'default',
                  display: '-webkit-box',
                  WebkitLineClamp: isTitleExpanded ? 'none' : 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: '1.4',
                  '&:hover': shouldShowExpandButton ? {
                    opacity: 0.8
                  } : {}
                }}
                onClick={shouldShowExpandButton ? handleTitleToggle : undefined}
              >
                {title}
              </Typography>
            </Tooltip>
            {shouldShowExpandButton && (
              <IconButton
                size="small"
                onClick={handleTitleToggle}
                sx={{
                  p: 0.5,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                {isTitleExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Document Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            position: 'relative'
          }}
        >
          {document?.url ? (
            isPdfDocument(document) ? (
              <ViewPdf
                currentLang={currentLang}
                pdfUrl={document.url}
                selectedCitation={selectedCitation}
                document={document}
                setDocument={setDocument}
                showTranslationMenu={showTranslationMenu}
              />
            ) : (
              <ViewHtml
                currentLang={currentLang}
                htmlContent={htmlContent}
                iframeRef={iframeRef}
                selectedCitation={selectedCitation}
                document={document}
                setDocument={setDocument}
                showTranslationMenu={showTranslationMenu}
              />
            )
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                height: '100%',
                bgcolor: theme.palette.background.default,
                borderRadius: '8px'
              }}
              dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
            >
              <Box
                sx={{
                  width: '120px',
                  height: '120px',
                  opacity: 0.8
                }}
              >
                <img
                  key={theme.palette.mode}
                  src={theme.palette.mode === 'dark' ? '/logo/logo-circle-dark-mode.svg' : '/logo/logo-circle.svg'}
                  alt="Lawis Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Box>
              <Box
                sx={{
                  marginTop: 2,
                  fontSize: '16px',
                  color: theme.palette.text.secondary,
                  textAlign: 'center',
                  maxWidth: '80%'
                }}
              >
                {t('choose_document')}
              </Box>
            </Box>
          )}

          {/* Loading overlay — shown on top of viewer so options bar stays visible */}
          {isLoadingDocument && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(0, 0, 0, 0.6)'
                  : 'rgba(255, 255, 255, 0.7)',
                zIndex: 10
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <CircularProgress size={50} color="primary" />
                {isTranslationInProgress && (
                  <TranslationProgress
                    currentProgress={translationProgress}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.document === nextProps.document &&
      prevProps.documentSourceUrl === nextProps.documentSourceUrl &&
      prevProps.isLoadingDocument === nextProps.isLoadingDocument &&
      prevProps.selectedCitation === nextProps.selectedCitation
    )
  }
)

export default DocumentViewer